import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import {
    ChevronDown, AlertCircle,
    CheckCircle,
    Upload, FileText, X, Plus
} from "lucide-react";

const LANGUAGES = ["English", "Hindi", "Punjabi", "French", "Spanish", "Arabic", "Chinese", "Russian"];

const StatementForm = ({
    statements, setStatements, narratives, setNarratives,
    eSignatureText, setESignatureText, signatureUrl, signatureUploading,
    getEmptyBlock, preview, uploadSignature, updateList, handleFileUpload, loading,
    setPreview, onSave
}) => {
    const [expandedSections, setExpandedSections] = useState({});
    const [localErrors, setLocalErrors] = useState({});

    const toggleSection = (section, index) => {
        setExpandedSections(prev => ({
            ...prev,
            [`${section}_${index}`]: !prev[`${section}_${index}`]
        }));
    };

    const removeFile = (index, fileIndex, setter) => {
        setter((prev) => {
            const updated = [...prev];
            updated[index].files.splice(fileIndex, 1);
            return [...updated];
        });
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate statements
        statements.forEach((stmt, i) => {
            if (stmt.language && !stmt.description) {
                newErrors[`stmt_desc_${i}`] = "Description is required when language is selected";
            }
        });

        // Validate narratives
        narratives.forEach((nar, i) => {
            if (nar.language && !nar.description) {
                newErrors[`nar_desc_${i}`] = "Description is required when language is selected";
            }
        });

        // Validate signature
        if (!eSignatureText.trim()) {
            newErrors.signature = "E-signature text is required";
        }
        if (!signatureUrl) {
            newErrors.signatureFile = "Signature file is required";
        }

        setLocalErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm() && onSave) {
            onSave();
        }
    };

    // If in preview mode
    if (preview) {
        return (
            <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
                <ToastContainer position="top-right" autoClose={3000} />

                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                                <FileText size={24} className="text-blue-300" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Statement & Narratives</h1>
                                <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Official Declarations & Supporting Documents</p>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">
                    {preview?.status === "approve" && (
                        <div className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-100 flex items-center gap-3">
                            <CheckCircle size={20} className="text-green-600 shrink-0" />
                            <p className="text-sm font-medium text-green-800">Declarations verified and approved</p>
                        </div>
                    )}

                    {/* Statements Section */}
                    {preview.statements?.length > 0 && (
                        <div className="mb-8">
                            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FileText size={18} className="text-blue-600" />
                                Official Statements
                            </h3>
                            <div className="space-y-4">
                                {preview.statements.map((st, i) => (
                                    <div key={i} className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="text-[10px] sm:text-xs font-bold text-blue-600 uppercase tracking-wider">
                                                Statement #{i + 1}
                                            </span>
                                            <span className="text-[10px] sm:text-xs font-semibold text-gray-600 bg-white px-2 py-0.5 rounded">
                                                {st.language}
                                            </span>
                                        </div>
                                        <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">{st.description}</p>
                                        {st.files?.length > 0 && (
                                            <div className="flex flex-wrap gap-2 pt-2">
                                                {st.files.map((file, fi) => (
                                                    <a key={fi} href={file.url} target="_blank" rel="noreferrer"
                                                        className="flex items-center gap-2 bg-white hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 transition-colors border border-gray-200">
                                                        <FileText size={12} className="text-blue-500" />
                                                        {file.fileName}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Narratives Section */}
                    {preview.narratives?.length > 0 && (
                        <div className="mb-8">
                            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FileText size={18} className="text-emerald-600" />
                                Supporting Narratives
                            </h3>
                            <div className="space-y-4">
                                {preview.narratives.map((nr, i) => (
                                    <div key={i} className="bg-gray-50 rounded-xl p-4 sm:p-5 border border-gray-200">
                                        <div className="flex justify-between items-start mb-3">
                                            <span className="text-[10px] sm:text-xs font-bold text-emerald-600 uppercase tracking-wider">
                                                Narrative #{i + 1}
                                            </span>
                                            <span className="text-[10px] sm:text-xs font-semibold text-gray-600 bg-white px-2 py-0.5 rounded">
                                                {nr.language}
                                            </span>
                                        </div>
                                        <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">{nr.description}</p>
                                        {nr.files?.length > 0 && (
                                            <div className="flex flex-wrap gap-2 pt-2">
                                                {nr.files.map((file, fi) => (
                                                    <a key={fi} href={file.url} target="_blank" rel="noreferrer"
                                                        className="flex items-center gap-2 bg-white hover:bg-gray-100 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 transition-colors border border-gray-200">
                                                        <FileText size={12} className="text-emerald-500" />
                                                        {file.fileName}
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Authorization Section */}
                    <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-xl p-5 sm:p-6 shadow-xl relative overflow-hidden">
                        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                            <div className="space-y-2 text-center md:text-left">
                                <p className="text-blue-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest">
                                    E-Signature Verification
                                </p>
                                <h4 className="text-lg sm:text-xl md:text-2xl font-serif italic tracking-wide">
                                    {preview.eSignatureText}
                                </h4>
                                <p className="text-blue-300/70 text-[9px] sm:text-[10px]">
                                    Digitally signed on {new Date(preview.createdAt).toLocaleString()}
                                </p>
                            </div>
                            {preview.signatureFile && (
                                <div className="bg-white p-2 rounded-lg shadow-inner">
                                    <img src={preview.signatureFile} alt="Signature" className="h-12 sm:h-16 w-auto object-contain" />
                                </div>
                            )}
                        </div>
                    </div>

                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-5xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10">
            <ToastContainer position="top-right" autoClose={3000} />

            {loading && (
                <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-center gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                        <p className="text-sm font-medium text-blue-600">Processing declarations...</p>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-800 rounded-t-xl sm:rounded-t-2xl md:rounded-t-3xl p-5 sm:p-6 md:p-8 text-white shadow-xl">
                <div className="flex items-center gap-3 sm:gap-4">
                    <div className="p-2 sm:p-3 bg-white/10 rounded-xl backdrop-blur-sm">
                        <FileText size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold">Statement & Narratives</h1>
                        <p className="text-blue-100/80 text-[10px] sm:text-xs mt-0.5">Provide your official declarations and supporting documents</p>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-b-xl sm:rounded-b-2xl md:rounded-b-3xl shadow-xl border-x border-b border-gray-100 p-5 sm:p-6 md:p-8">

                {/* Error Display */}
                {Object.keys(localErrors).length > 0 && (
                    <div className="mb-6 bg-rose-50 border border-rose-200 p-4 rounded-xl animate-in fade-in slide-in-from-top-4">
                        <div className="flex items-center gap-2 text-rose-700 font-medium mb-2">
                            <AlertCircle size={18} /> Please correct the following:
                        </div>
                        <ul className="space-y-1">
                            {Object.values(localErrors).map((err, i) => (
                                <li key={i} className="text-rose-600 text-[11px] flex items-center gap-1">
                                    <AlertCircle size={10} /> {err}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8 sm:space-y-10">
                    <DynamicSection
                        title="Official Statements"
                        subtitle="Declare your formal statements in your preferred language."
                        list={statements}
                        update={(index, field, value) => {
                            const updated = [...statements];
                            updated[index][field] = value;
                            setStatements(updated);
                        }}
                        add={() => setStatements([...statements, getEmptyBlock()])}
                        remove={(i) => setStatements(statements.filter((_, idx) => idx !== i))}
                        handleFileUpload={(file, i) => handleFileUpload(file, i, "statements")}
                        removeFile={(i, fi) => removeFile(i, fi, setStatements)}
                        expandedSections={expandedSections}
                        toggleSection={(i) => toggleSection('stmt', i)}
                    />

                    <DynamicSection
                        title="Supporting Narratives"
                        subtitle="Additional stories or background information to support your case."
                        list={narratives}
                        update={(index, field, value) => {
                            const updated = [...narratives];
                            updated[index][field] = value;
                            setNarratives(updated);
                        }}
                        add={() => setNarratives([...narratives, getEmptyBlock()])}
                        remove={(i) => setNarratives(narratives.filter((_, idx) => idx !== i))}
                        handleFileUpload={(file, i) => handleFileUpload(file, i, "narratives")}
                        removeFile={(i, fi) => removeFile(i, fi, setNarratives)}
                        expandedSections={expandedSections}
                        toggleSection={(i) => toggleSection('nar', i)}
                        isNarrative
                    />

                    {/* Signature Section */}
                    <div className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 shadow-sm">
                        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                            <span className="p-1.5 bg-gray-100 rounded-lg">🖋️</span>
                            Final Authorization
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                            <div className="space-y-2">
                                <label className="block text-xs sm:text-sm font-bold text-gray-700">
                                    E-Signature (Type Full Name) <span className="text-rose-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={eSignatureText}
                                    onChange={(e) => setESignatureText(e.target.value)}
                                    placeholder="John Doe"
                                    className={`w-full bg-gray-50 border rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm
                                        ${localErrors.signature ? "border-rose-300 bg-rose-50/30" : "border-gray-200"}`}
                                />
                                {localErrors.signature && (
                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors.signature}</p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="block text-xs sm:text-sm font-bold text-gray-700">
                                    Upload Signature File <span className="text-rose-500">*</span>
                                </label>
                                <div className={`border-2 border-dashed rounded-xl p-4 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer relative
                                    ${localErrors.signatureFile ? "border-rose-300 bg-rose-50/30" : "border-gray-300"}`}>
                                    <input
                                        type="file"
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                        accept="image/*"
                                        onChange={(e) => uploadSignature(e.target.files[0])}
                                    />
                                    {signatureUrl ? (
                                        <img src={signatureUrl} alt="signature" className="h-12 sm:h-16 object-contain" />
                                    ) : (
                                        <div className="text-center">
                                            <Upload size={24} className="text-gray-400 mx-auto mb-1" />
                                            <span className="text-gray-400 text-[10px] sm:text-xs">Click or drag to upload</span>
                                        </div>
                                    )}
                                </div>
                                {signatureUploading && (
                                    <p className="text-xs text-blue-500 animate-pulse flex items-center gap-1">
                                        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                        Uploading signature...
                                    </p>
                                )}
                                {localErrors.signatureFile && (
                                    <p className="text-rose-500 text-[10px] mt-1">{localErrors.signatureFile}</p>
                                )}
                            </div>
                        </div>
                    </div>


                </form>
            </div>
        </div>
    );
};

const DynamicSection = ({ title, subtitle, list, update, add, remove, handleFileUpload, removeFile, expandedSections, toggleSection, isNarrative }) => (
    <div className="space-y-5 sm:space-y-6">
        <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-800">{title}</h3>
            <p className="text-gray-500 text-xs sm:text-sm">{subtitle}</p>
        </div>

        {list.length === 0 ? (
            <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <FileText size={32} className="text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">No {title.toLowerCase()} added</p>
                <button
                    type="button"
                    onClick={add}
                    className="mt-2 text-blue-600 text-xs font-medium flex items-center gap-1 mx-auto"
                >
                    <Plus size={14} /> Add your first {title.toLowerCase().slice(0, -1)}
                </button>
            </div>
        ) : (
            list.map((item, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-xl sm:rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                    {/* Card Header - Clickable on mobile */}
                    <div
                        className="flex justify-between items-center p-4 sm:p-5 cursor-pointer md:cursor-default bg-gray-50/50"
                        onClick={() => toggleSection(index)}
                    >
                        <div className="flex items-center gap-2">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold
                                ${isNarrative ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                {index + 1}
                            </div>
                            <h4 className="font-semibold text-gray-800 text-sm">
                                {title.slice(0, -1)} #{index + 1}
                            </h4>
                        </div>
                        <div className="flex items-center gap-2">
                            {item.language && (
                                <span className={`text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded
                                    ${isNarrative ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {item.language}
                                </span>
                            )}
                            <button className="md:hidden text-gray-400">
                                <ChevronDown size={16} className={`transition-transform ${expandedSections[`${isNarrative ? 'nar' : 'stmt'}_${index}`] ? 'rotate-180' : ''}`} />
                            </button>
                            {list.length > 1 && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        remove(index);
                                    }}
                                    className="text-rose-400 hover:text-rose-600 p-1 transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Card Content */}
                    <div className={`
                        transition-all duration-300 overflow-hidden
                        ${expandedSections[`${isNarrative ? 'nar' : 'stmt'}_${index}`] !== false ? 'max-h-[2000px] opacity-100' : 'max-h-0 md:max-h-[2000px] md:opacity-100'}
                    `}>
                        <div className="p-4 sm:p-5 pt-0 sm:pt-0 border-t border-gray-100">
                            <div className="grid grid-cols-1 gap-4 sm:gap-5">
                                <div>
                                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                                        Language <span className="text-rose-500">*</span>
                                    </label>
                                    <select
                                        value={item.language}
                                        onChange={(e) => update(index, "language", e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm appearance-none cursor-pointer"
                                    >
                                        <option value="">Select Language</option>
                                        {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-1.5">
                                        Description (Max 5000 chars) <span className="text-rose-500">*</span>
                                    </label>
                                    <textarea
                                        value={item.description}
                                        rows={4}
                                        onChange={(e) => update(index, "description", e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 sm:py-3 focus:border-blue-500 focus:bg-white outline-none transition-all text-sm resize-none"
                                        placeholder={`Describe your ${title.toLowerCase().slice(0, -1)} here...`}
                                        maxLength={5000}
                                    />
                                    <p className="text-right text-[10px] text-gray-400 mt-1">
                                        {item.description?.length || 0}/5000 characters
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-xs sm:text-sm font-bold text-gray-700 mb-2">
                                        Supporting Documents
                                    </label>
                                    <div className="flex flex-wrap gap-2">
                                        <label className="relative overflow-hidden bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-lg px-3 py-1.5 transition-all cursor-pointer">
                                            <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
                                                <Upload size={12} /> Upload File
                                            </span>
                                            <input
                                                type="file"
                                                className="absolute inset-0 opacity-0 cursor-pointer"
                                                onChange={(e) => handleFileUpload(e.target.files[0], index)}
                                            />
                                        </label>

                                        {item.files?.filter(Boolean).map((f, i) => (
                                            <div key={i} className="flex items-center gap-1.5 bg-gray-800 text-white px-2 py-1 rounded-lg text-[10px] font-medium">
                                                <FileText size={10} />
                                                <span className="truncate max-w-[100px] sm:max-w-[150px] cursor-pointer" onClick={() => window.open(f.url)}>
                                                    {f.fileName}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFile(index, i)}
                                                    className="hover:text-rose-400 transition-colors"
                                                >
                                                    <X size={12} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ))
        )}

        <button
            type="button"
            onClick={add}
            className="w-full py-2.5 sm:py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-semibold text-xs sm:text-sm hover:border-blue-500 hover:text-blue-600 transition-all flex items-center justify-center gap-2"
        >
            <Plus size={16} /> Add {title.slice(0, -1)}
        </button>
    </div>
);

export default StatementForm;